import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FaBed, FaFileExcel, FaSync, FaBan } from "react-icons/fa";
import conciliacionCamasApi from "../../../services/api/conciliacionCamasApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";

const ESTADOS_EXCEL = [
  { value: "", label: "Todos" },
  { value: "OK", label: "OK (activa)" },
  { value: "NO_EXISTE", label: "No existe" },
  { value: "INACTIVA", label: "Inactiva" },
  { value: "ERROR_FILA", label: "Error en fila" },
];

const badgeVariantExcel = (estado) => {
  switch (estado) {
    case "OK":
      return "success";
    case "NO_EXISTE":
      return "danger";
    case "INACTIVA":
      return "warning";
    case "ERROR_FILA":
      return "secondary";
    default:
      return "secondary";
  }
};

const ConciliacionCamas = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inactivando, setInactivando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorMensaje, setErrorMensaje] = useState("");
  const [filtroEstadoExcel, setFiltroEstadoExcel] = useState("");
  const [busquedaExcel, setBusquedaExcel] = useState("");
  const [busquedaSobrantes, setBusquedaSobrantes] = useState("");
  const [seleccionadas, setSeleccionadas] = useState([]);

  useEffect(() => {
    try {
      const storedProject = localStorage.getItem("selectedProject");
      if (storedProject && storedProject !== "undefined") {
        setSelectedProject(JSON.parse(storedProject));
      }
    } catch (error) {
      console.error("Error al cargar selectedProject:", error);
      toast.error("Error al cargar la información del proyecto");
    }
  }, []);

  const filasExcelFiltradas = useMemo(() => {
    const filas = resultado?.filas_excel || [];
    return filas.filter((f) => {
      if (filtroEstadoExcel && f.estado !== filtroEstadoExcel) return false;
      if (!busquedaExcel.trim()) return true;
      const q = busquedaExcel.trim().toLowerCase();
      const texto = [f.zona, f.modulo, f.habitacion, f.cama, f.mensaje]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return texto.includes(q);
    });
  }, [resultado, filtroEstadoExcel, busquedaExcel]);

  const sobrantesFiltrados = useMemo(() => {
    const filas = resultado?.camas_sobrantes || [];
    return filas.filter((f) => {
      if (!busquedaSobrantes.trim()) return true;
      const q = busquedaSobrantes.trim().toLowerCase();
      const texto = [f.zona, f.modulo, f.habitacion, f.cama, f.estado_cama, f.mensaje]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return texto.includes(q);
    });
  }, [resultado, busquedaSobrantes]);

  const sobrantesInactivables = useMemo(
    () => sobrantesFiltrados.filter((f) => f.puede_inactivar),
    [sobrantesFiltrados]
  );

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const ext = selected.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      toast.error("Seleccione un archivo Excel (.xlsx o .xls)");
      e.target.value = "";
      return;
    }

    setFile(selected);
    setResultado(null);
    setErrorMensaje("");
    setSeleccionadas([]);
  };

  const aplicarResultado = (data, silent = false) => {
    if (!data || typeof data !== "object") {
      setErrorMensaje("Respuesta inválida del servidor");
      if (!silent) toast.error("Respuesta inválida del servidor");
      return;
    }

    setResultado(data);
    setErrorMensaje(data.success === false ? data.message || "Error en la conciliación" : "");

    if (!silent) {
      if (data.success === false) {
        toast.warning(data.message || "No se pudo completar la conciliación");
      } else {
        toast.success(data.message || "Conciliación completada");
      }
    }
  };

  const conciliar = async (silent = false) => {
    if (!selectedProject?.id) {
      if (!silent) toast.error("Seleccione un proyecto");
      return;
    }
    if (!file) {
      if (!silent) toast.warning("Seleccione un archivo Excel");
      return;
    }

    setLoading(true);
    setErrorMensaje("");
    if (!silent) setSeleccionadas([]);
    try {
      const response = await conciliacionCamasApi.conciliar(
        file,
        selectedProject.id
      );
      aplicarResultado(response.data, silent);
    } catch (error) {
      console.error(error);
      const detail = error.response?.data?.detail;
      const mensaje =
        (typeof detail === "string" && detail) ||
        error.response?.data?.message ||
        error.message ||
        "Error al conciliar";

      setErrorMensaje(mensaje);
      setResultado(null);

      if (!silent) {
        toast.error(mensaje);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSeleccion = (camaId) => {
    setSeleccionadas((prev) =>
      prev.includes(camaId)
        ? prev.filter((id) => id !== camaId)
        : [...prev, camaId]
    );
  };

  const toggleSeleccionarTodas = () => {
    const ids = sobrantesInactivables.map((f) => f.cama_id);
    const todasMarcadas = ids.every((id) => seleccionadas.includes(id));
    if (todasMarcadas) {
      setSeleccionadas((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSeleccionadas((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const inactivarCamas = async (camaIds) => {
    if (!camaIds.length) {
      toast.warning("No hay camas seleccionadas");
      return;
    }

    const ok = window.confirm(
      `¿Inactivar ${camaIds.length} cama(s)? No aparecerán en reservas ni reportes operativos.`
    );
    if (!ok) return;

    setInactivando(true);
    try {
      const response = await conciliacionCamasApi.inactivarCamas(camaIds);
      const data = response.data;
      const fallidas = (data.resultados || []).filter((r) => !r.success);

      if (fallidas.length) {
        toast.warning(
          `${data.total_inactivadas} inactivada(s), ${data.total_fallidas} con error`
        );
      } else {
        toast.success(data.message || "Camas inactivadas");
      }

      if (file) {
        setSeleccionadas([]);
        await conciliar(true);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail ||
          error.message ||
          "Error al inactivar camas"
      );
    } finally {
      setInactivando(false);
    }
  };

  const exportarExcel = () => {
    if (!resultado) {
      toast.warning("No hay resultados para exportar");
      return;
    }

    const wsExcel = XLSX.utils.json_to_sheet(
      (resultado.filas_excel || []).map((f) => ({
        Fila: f.fila,
        Estado: f.estado,
        Zona: f.zona,
        Módulo: f.modulo,
        Habitación: f.habitacion,
        Cama: f.cama,
        "ID Cama": f.cama_id ?? "",
        Mensaje: f.mensaje,
      }))
    );

    const wsSobrantes = XLSX.utils.json_to_sheet(
      (resultado.camas_sobrantes || []).map((f) => ({
        "ID Cama": f.cama_id,
        Zona: f.zona,
        Módulo: f.modulo,
        Habitación: f.habitacion,
        Cama: f.cama,
        "Estado cama": f.estado_cama,
        "Puede inactivar": f.puede_inactivar ? "Sí" : "No",
        Reservas: f.reservas,
        "Check-ins": f.check_ins,
        Mensaje: f.mensaje,
      }))
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsExcel, "Excel validado");
    XLSX.utils.book_append_sheet(wb, wsSobrantes, "Sobrantes sistema");

    if (resultado.resumen) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet([resultado.resumen]),
        "Resumen"
      );
    }

    const fecha = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `conciliacion_camas_${fecha}.xlsx`);
    toast.success("Excel exportado");
  };

  const resumen = resultado?.resumen;

  return (
    <div className="dh-reporte-bg">
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding-bottom: 2rem;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          .dh-card-header-warning {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; opacity: 0.95; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; opacity: 0.95; }
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 8px 14px;
            font-weight: 600;
          }
          .btn-dh-danger:hover { color: white; opacity: 0.95; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control { border-radius: 10px; }
          .dh-alert-info {
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            border: 1px solid #93c5fd;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            color: #1e40af;
          }
          .stat-box {
            text-align: center;
            padding: 1rem;
            border-radius: 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
          }
          .stat-box h4 { margin: 0; font-weight: 700; }
          .stat-box small { color: #64748b; }
          .table-conciliacion { font-size: 0.875rem; }
          .table-conciliacion th {
            background: #f1f5f9;
            position: sticky;
            top: 0;
            z-index: 1;
          }
        `}
      </style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaBed className="me-2" />
            Conciliación de Camas
          </h1>
          <p className="dh-main-subtitle">
            Valide un inventario Excel contra las camas activas del proyecto y
            gestione las camas sobrantes en el sistema
          </p>
        </Container>
      </div>

      <Container>
        <Row className="mb-4">
          <Col>
            <div className="dh-alert dh-alert-info">
              Cargue un Excel con columnas <strong>ZONA</strong>,{" "}
              <strong>MODULO</strong>, <strong>HAB</strong> (o HABITACION) y{" "}
              <strong>CAMA</strong>. El sistema verificará que cada cama exista
              y esté activa en una zona activa, y mostrará las camas activas de zonas
              activas que no figuren en el archivo para poder inactivarlas.
            </div>
            {!selectedProject?.id && (
              <div className="alert alert-warning mt-3 mb-0">
                Debe seleccionar un proyecto antes de conciliar.
              </div>
            )}
            {errorMensaje && (
              <div className="alert alert-danger mt-3 mb-0">{errorMensaje}</div>
            )}
          </Col>
        </Row>

        <Card className="dh-card mb-4">
          <Card.Header className="dh-card-header dh-card-header-primary">
            Cargar inventario Excel
          </Card.Header>
          <Card.Body>
            <Row className="align-items-end g-3">
              <Col md={6}>
                <Form.Label className="dh-form-label">Archivo Excel</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  className="dh-form-control"
                  onChange={handleFileChange}
                />
                {file && (
                  <small className="text-muted d-block mt-1">{file.name}</small>
                )}
              </Col>
              <Col md={6} className="d-flex gap-2 flex-wrap">
                <Button
                  className="btn-dh-primary"
                  onClick={conciliar}
                  disabled={loading || !file}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Conciliando...
                    </>
                  ) : (
                    <>
                      <FaSync className="me-2" />
                      Conciliar
                    </>
                  )}
                </Button>
                {resultado && (
                  <Button className="btn-dh-secondary" onClick={exportarExcel}>
                    <FaFileExcel className="me-2" />
                    Exportar resultados
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {resumen && (
          <Row className="mb-4 g-3">
            <Col md={2} sm={4} xs={6}>
              <div className="stat-box">
                <h4>{resumen.total_filas_excel}</h4>
                <small>Filas Excel</small>
              </div>
            </Col>
            <Col md={2} sm={4} xs={6}>
              <div className="stat-box">
                <h4 className="text-success">{resumen.filas_ok}</h4>
                <small>OK activas</small>
              </div>
            </Col>
            <Col md={2} sm={4} xs={6}>
              <div className="stat-box">
                <h4 className="text-danger">{resumen.filas_no_existen}</h4>
                <small>No existen</small>
              </div>
            </Col>
            <Col md={2} sm={4} xs={6}>
              <div className="stat-box">
                <h4 className="text-warning">{resumen.filas_inactivas}</h4>
                <small>Inactivas en Excel</small>
              </div>
            </Col>
            <Col md={2} sm={4} xs={6}>
              <div className="stat-box">
                <h4>{resumen.total_camas_activas_sistema}</h4>
                <small>Activas en sistema</small>
              </div>
            </Col>
            <Col md={2} sm={4} xs={6}>
              <div className="stat-box">
                <h4 className="text-primary">{resumen.camas_sobrantes_en_sistema}</h4>
                <small>Sobrantes</small>
              </div>
            </Col>
          </Row>
        )}

        {resultado && (
          <>
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header dh-card-header-primary">
                Validación del Excel ({filasExcelFiltradas.length} filas)
              </Card.Header>
              <Card.Body>
                <Row className="mb-3 g-2">
                  <Col md={4}>
                    <Form.Label className="dh-form-label">Estado</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={filtroEstadoExcel}
                      onChange={(e) => setFiltroEstadoExcel(e.target.value)}
                    >
                      {ESTADOS_EXCEL.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={8}>
                    <Form.Label className="dh-form-label">Buscar</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      placeholder="Zona, módulo, habitación, cama..."
                      value={busquedaExcel}
                      onChange={(e) => setBusquedaExcel(e.target.value)}
                    />
                  </Col>
                </Row>

                <div style={{ maxHeight: "420px", overflow: "auto" }}>
                  <Table striped bordered hover responsive className="table-conciliacion mb-0">
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Estado</th>
                        <th>Zona</th>
                        <th>Módulo</th>
                        <th>Hab.</th>
                        <th>Cama</th>
                        <th>Mensaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filasExcelFiltradas.map((f) => (
                        <tr key={`excel-${f.fila}`}>
                          <td>{f.fila}</td>
                          <td>
                            <Badge bg={badgeVariantExcel(f.estado)}>{f.estado}</Badge>
                          </td>
                          <td>{f.zona}</td>
                          <td>{f.modulo}</td>
                          <td>{f.habitacion}</td>
                          <td>{f.cama}</td>
                          <td>{f.mensaje}</td>
                        </tr>
                      ))}
                      {!filasExcelFiltradas.length && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            Sin filas para mostrar
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header dh-card-header-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span>
                  Camas activas en sistema no incluidas en el Excel (
                  {sobrantesFiltrados.length})
                </span>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    className="btn-dh-danger"
                    disabled={
                      inactivando || seleccionadas.length === 0
                    }
                    onClick={() => inactivarCamas(seleccionadas)}
                  >
                    {inactivando ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <FaBan className="me-1" />
                        Inactivar seleccionadas ({seleccionadas.length})
                      </>
                    )}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={8}>
                    <Form.Control
                      className="dh-form-control"
                      placeholder="Buscar camas sobrantes..."
                      value={busquedaSobrantes}
                      onChange={(e) => setBusquedaSobrantes(e.target.value)}
                    />
                  </Col>
                </Row>

                <div style={{ maxHeight: "480px", overflow: "auto" }}>
                  <Table striped bordered hover responsive className="table-conciliacion mb-0">
                    <thead>
                      <tr>
                        <th>
                          <Form.Check
                            type="checkbox"
                            checked={
                              sobrantesInactivables.length > 0 &&
                              sobrantesInactivables.every((f) =>
                                seleccionadas.includes(f.cama_id)
                              )
                            }
                            disabled={!sobrantesInactivables.length}
                            onChange={toggleSeleccionarTodas}
                            title="Seleccionar todas las que se pueden inactivar"
                          />
                        </th>
                        <th>Zona</th>
                        <th>Módulo</th>
                        <th>Hab.</th>
                        <th>Cama</th>
                        <th>Estado</th>
                        <th>Reservas / Check-in</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sobrantesFiltrados.map((f) => (
                        <tr key={`sob-${f.cama_id}`}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={seleccionadas.includes(f.cama_id)}
                              disabled={!f.puede_inactivar}
                              onChange={() => toggleSeleccion(f.cama_id)}
                            />
                          </td>
                          <td>{f.zona}</td>
                          <td>{f.modulo}</td>
                          <td>{f.habitacion}</td>
                          <td>{f.cama}</td>
                          <td>{f.estado_cama}</td>
                          <td>
                            {f.reservas} / {f.check_ins}
                            {!f.puede_inactivar && (
                              <div>
                                <small className="text-danger">{f.mensaje}</small>
                              </div>
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              className="btn-dh-danger"
                              disabled={!f.puede_inactivar || inactivando}
                              onClick={() => inactivarCamas([f.cama_id])}
                            >
                              Inactivar
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!sobrantesFiltrados.length && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            No hay camas sobrantes. El inventario coincide con el sistema.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default ConciliacionCamas;
